/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { InjectionToken, ViewContainerRef } from '@angular/core';

import { ColDef, GridApi, GridOptions, type ProcessRowParams } from 'ag-grid-community';

import { GridSharedBeans } from '../components';
import { GridOptionsExt } from './ext/grid-options-ext';

/**
 * Parameters to be used on initialization stage.
 */
export interface GridFeatureInitParams {
  viewContainerRef: ViewContainerRef;
  nativeElement: HTMLElement;
}

/**
 * Parameters to be used on ready stage
 */
export interface GridFeatureReadyParams {
  api: GridApi;
  columnApi: GridApi;
  sharedBeans: GridSharedBeans;
}

/**
 * {@link GridColumnFeature} allows to extend column behaviour by
 * encapsulating reusable approaches into pluggable features.
 */
export interface GridColumnFeature {
  enhanceColumn(colDef: ColDef): void;

  afterGridOptionsEnhanced?(options: GridOptions): void;

  onDestroy?(): void;
}

/**
 * {@link GridFeature} allows to extend grid behaviour by
 * encapsulating reusable approaches into pluggable features.
 *
 * Each feature has following lifecycle stages
 * - initialization. On this stage developer can init feature before enhancing has started.
 * - enhancing. On this stage developer can enhance {@link GridOptions} passed into method as parameter.
 * - ready stage. On this stage grid is ready to be used and it takes {@link GridApi} (and legacy {@link GridFeatureReadyParams.columnApi} alias).
 * - destroy stage. On this stage feature can clean allocated resources before grid is destroyed.
 */
export interface GridFeature {
  /**
   * Order of initialization. Feature having lower order loads first.
   * If order is not specified it means order = 0.
   */
  order?: number;

  /**
   * Seed beans to be injected into ag-grid DI context
   */
  seedBeans?: Hash<any>;

  /**
   * Method of initialization stage that allows to initialize feature
   *
   * @param params
   */
  initGrid(params: GridFeatureInitParams): void;

  /**
   * Method of enhance stage. On this stage developer can enhance passed {@link GridOptions}
   *
   * @param options
   */
  enhanceGridOptions(options: GridOptions | GridOptionsExt): void;

  /**
   * Method of ready stage. This method is called when grid is ready to be used.
   *
   * @param params
   */
  onGridReady(params: GridFeatureReadyParams): void;

  /**
   * Method of destroy stage. Implementation of this method allows to clean resources
   * allocated by the feature.
   */
  onDestroy(): void;

  process?(params: ProcessRowParams): void;
}

export const GRID_COLUMN_FEATURE = new InjectionToken('GridColumnFeature');

export const GRID_FEATURE = new InjectionToken('GridFeature');
